import { getRequestClient, type operation, type q } from "@repo/client";
import { batch, createResource } from "solid-js";
import { createStore } from "solid-js/store";

export type KnowledgeServiceDeps = {
  getAuthToken: () => string | undefined;
  isIdentityConnecting: () => boolean;
};

export type KnowledgeService = ReturnType<typeof useKnowledgeService>;

export const useKnowledgeService = (deps: KnowledgeServiceDeps) => {
  const client = getRequestClient(deps.getAuthToken);

  const [assetsCache, setAssetsCache] = createStore({
    byId: {} as Record<string, q.ControlplaneSsAsset>,
  });

  const getAssetById = (id: string) => assetsCache.byId[id];
  const updateAsset = (asset: q.ControlplaneSsAsset) => setAssetsCache("byId", asset.id, asset);

  const [data, setData] = createStore({
    queuedKnowledgeChange: {
      added: [] as string[],
      removed: [] as string[],
    },
  });

  const resetQueuedKnowledgeChange = () =>
    setData("queuedKnowledgeChange", {
      added: [],
      removed: [],
    });
  const addQueuedKnowledgeChange = (asset: string) =>
    setData("queuedKnowledgeChange", (queue) => {
      if (queue.removed.includes(asset)) {
        return {
          added: queue.added,
          removed: queue.removed.filter((r) => r !== asset),
        };
      }
      return {
        added: [...queue.added, asset],
        remove: queue.removed,
      };
    });
  const removeQueuedKnowledgeChange = (asset: string) =>
    setData("queuedKnowledgeChange", (queue) => {
      if (queue.added.includes(asset)) {
        return {
          removed: queue.removed,
          added: queue.added.filter((r) => r !== asset),
        };
      }
      return {
        removed: [...queue.removed, asset],
        added: queue.added,
      };
    });

  return {
    getAssetById,
    updateAsset,
    queuedKnowledgeChange: () => data.queuedKnowledgeChange,
    resetQueuedKnowledgeChange,
    addQueuedKnowledgeChange,
    removeQueuedKnowledgeChange,
    assetsCache,
    setAssetsCache,
  };
};
